import React, { useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { Button, Checkbox, H4, HR, Label, Text, TooltipIcon } from 'components';
import { Card } from 'components/Card';
import { Icon } from 'components/Icon';
import { Tag } from 'components/Tag';
import { theme, toRem } from 'design';
import { Appearance, Size } from 'design/types';
import { ICategoryGroup, ICheck, IPOSTicketSplitOverride, IPOSTicketSplitOverrideShare, RuleFundSource, TicketOverrideType } from 'types';
import { centsToDollarsDisplay } from 'utils/currency';
import { getDateTimeWithoutZone } from 'utils/date';
import CheckDetails from './CheckDetails';

const CHECK_LIST_ITEM_BREAKPOINT = 1100;
interface ICheckListItemProps {
  check: ICheck;
  categoryGroups: ICategoryGroup[];
  creditCardFee?: number;
  timeZone: string;
  dataCy?: string;
  onEditAmounts(check: ICheck): void;
  onSelect(check: ICheck, selected: boolean): void;
}

const Layout = styled(Card)<{ ignored?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0;
  background: ${theme.color.neutral[50]};
  overflow: visible;
  margin-bottom: ${toRem(12)};
  min-height: auto;
  min-width: fit-content;

  ${({ ignored }) =>
    ignored &&
    css`
      border: 1px solid ${theme.color.neutral[300]};
      background-color: ${theme.color.neutral[200]};
    `}

  :not(:last-child) {
    margin-bottom: ${toRem(20)};
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 2fr 2fr 2fr 1fr 1fr;
  grid-gap: ${toRem(8)};
  height: auto;
`;

const TopRow = styled(Row)`
  grid-template-columns: auto ${toRem(60)} ${toRem(34)};
  padding: ${toRem(22)} ${toRem(24)} ${toRem(8)} ${toRem(22)};
  width: 100%;

  @media (min-width: ${theme.breakpoint.med}px) {
    padding: ${toRem(22)} ${toRem(24)} ${toRem(22)} ${toRem(22)};
  }
`;

const MobileTopRow = styled(Row)`
  display: none;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  align-items: start;
  padding: 0 ${toRem(22)};
  margin-bottom: ${toRem(12)};

  label {
    min-height: 0 !important;
  }

  p {
    min-height: 0 !important;
    word-break: break-all;
    width: 100%;
  }

  @media (max-width: ${CHECK_LIST_ITEM_BREAKPOINT}px) {
    display: grid;
  }
`;

const MainContent = styled(Row)`
  grid-template-columns: ${toRem(36)} 3fr 0fr 0fr 0fr 2fr;
  gap: ${toRem(8)};
  align-items: center;
  margin-bottom: ${toRem(10)};

  @media (min-width: ${theme.breakpoint.med}px) {
    grid-template-columns: ${toRem(36)} 3fr 4fr 2fr 2fr 2fr;
    margin-bottom: 0;
    gap: ${toRem(16)};
  }
`;

const Controls = styled(Row)`
  grid-template-columns: 1fr 1fr;
  align-items: center;
  grid-gap: ${toRem(12)};

  @media (min-width: ${theme.breakpoint.med}px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const CheckInfoWrapper = styled.div`
  align-items: center;

  p {
    white-space: nowrap;
  }
`;

const TopRowItem = styled.div<{ hideOnMobile?: boolean }>`
  display: flex;
  flex-flow: column;
  justify-content: end;

  @media (max-width: ${CHECK_LIST_ITEM_BREAKPOINT}px) {
    ${({ hideOnMobile }) =>
      hideOnMobile &&
      css`
        display: none;
      `}
  }

  @media (min-width: ${theme.breakpoint.small}px) {
    justify-content: center;
  }

  & * {
    font-family: ${theme.font.family.euclid};
    font-weight: ${theme.font.weight.regular};
  }

  & > p {
    // required to override existing specificity
    font-size: ${toRem(14)};
    font-weight: ${theme.font.weight.medium};
    max-width: 100%;

    &.word-break {
      word-break: break-all;
    }

    &.check-time {
      font-size: ${toRem(11)};
      font-weight: ${theme.font.weight.regular};
      color: ${theme.color.neutral[600]};
    }
  }

  & > label {
    // required to override existing specificity
    margin-bottom: ${toRem(8)};
    color: ${theme.color.neutral[500]};
    font-weight: ${theme.font.weight.medium};
    font-size: ${toRem(11)};

    &.display-id {
      position: relative;
      top: ${toRem(4)};
      font-size: ${toRem(16)};
      font-weight: ${theme.font.weight.bold};
      color: ${theme.color.neutral[900]};
      word-break: break-all;
      width: 100%;
    }
  }

  & > h4 {
    // required to override existing specificity
    margin-bottom: ${toRem(12)};
    font-weight: ${theme.font.weight.bold};
  }

  @media (min-width: ${theme.breakpoint.med}px) {
    align-items: flex-start;
  }
`;

const StyledTag = styled(Tag)<{ ignored?: boolean }>`
  background-color: ${({ ignored }) => (ignored ? theme.color.neutral[100] : theme.color.neutral[200])};
  margin-bottom: ${toRem(4)};
  display: inline-block;
  overflow: hidden;
  text-overflow: ellipsis;
  align-content: center;
  white-space: nowrap;

  @media (max-width: 1600px) {
    max-width: ${toRem(130)};
  }
`;

const TooltipWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;

  & > p {
    // required to override existing specificity
    font-size: ${toRem(14)};
    font-weight: ${theme.font.weight.medium};

    &.check-time {
      font-size: ${toRem(11)};
      font-weight: ${theme.font.weight.regular};
      color: ${theme.color.neutral[600]};
    }
  }
`;

const ToolTipMessage = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: start;
  justify-content: space-evenly;
  text-align: left;
`;

const ToolTipMessageItem = styled.div`
  display: grid;
  grid-template-columns: ${toRem(50)} auto;
  grid-gap: ${toRem(4)};
  width: 100%;

  & > p {
    text-align: left;
    font-size: ${toRem(11)};
    color: ${theme.color.white};

    &:first-child {
      width: ${toRem(80)};
      color: ${theme.color.neutral[400]};
    }

    &:last-child {
      text-align: left;
    }
  }
`;

const HeaderRow = styled.div<{ ignored?: boolean }>`
  display: flex;
  justify-content: center;
  text-align: center;
  padding: ${toRem(8)} ${toRem(20)};
  border-top-right-radius: ${toRem(11)};
  border-top-left-radius: ${toRem(11)};
  border-bottom: 1px solid ${theme.color.neutral[200]};
  background-color: ${theme.color.warning.light};

  p {
    font-size: ${toRem(14)};

    &.action {
      margin-left: ${toRem(12)};
      color: ${theme.color.primary[600]};

      &:hover {
        cursor: pointer;
      }
    }
  }

  ${({ ignored }) =>
    ignored &&
    css`
      border-bottom: 1px solid ${theme.color.neutral[300]};
      background-color: ${theme.color.neutral[200]};
    `}
`;

const DropdownIconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: ${toRem(32)};
  height: ${toRem(32)};
  margin-right: ${toRem(8)};
  background: ${theme.color.white};
  border: 1px solid ${theme.color.neutral[300]};
  border-radius: 50%;

  &:hover {
    cursor: pointer;
  }
`;

const DropdownIcon = styled(Icon)`
  &.open {
    transform: rotate(180deg);
  }
`;

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 ${toRem(24)} ${toRem(18)} ${toRem(18)};
  padding: ${toRem(16)};
  border-radius: ${toRem(8)};
  border: 1px solid ${theme.color.neutral[200]};
  background: ${theme.color.white};

  hr {
    margin: ${toRem(8)} 0;
    border-top: ${toRem(1)} solid ${theme.color.neutral[200]};
  }
`;

const DetailsContainer = styled(Row)`
  display: flex;
  flex-direction: column;
  justify-content: center;

  h4 {
    font-size: ${toRem(12)};
    font-weight: ${theme.font.weight.semiBold};
    margin-bottom: ${toRem(4)};
  }
`;

const SplitDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr ${toRem(70)};
  grid-gap: ${toRem(8)};
  width: 100%;

  & > label {
    color: ${theme.color.neutral[500]};
    font-size: ${toRem(12)};

    &.align-right {
      text-align: right;
    }
  }

  & > p.align-right {
    text-align: right;
  }
`;

const displayShares = (shares: IPOSTicketSplitOverrideShare[], title?: string) => (
  <DetailsContainer>
    {title && <H4 fontWeight="medium">{title}</H4>}
    <SplitDetails>
      <Label>Recipient</Label>
      <Label>Split</Label>
      <Label>Gross</Label>
      <Label>CC Fees</Label>
      <Label>Net</Label>
      {shares.map((share, index) => (
        <React.Fragment key={`split-details-${index}`}>
          <Text size="xsmall">{share.participant.name}</Text>
          <Text size="xsmall">{share.percent}%</Text>
          <Text size="xsmall">{centsToDollarsDisplay(share.gross)}</Text>
          <Text size="xsmall">{centsToDollarsDisplay(share.gross - share.tip - share.autoGratuity)}</Text>
          <Text size="xsmall">{centsToDollarsDisplay(share.tip + share.autoGratuity)}</Text>
        </React.Fragment>
      ))}
    </SplitDetails>
  </DetailsContainer>
);

const CheckListItem: React.FC<ICheckListItemProps> = ({ check, categoryGroups, creditCardFee, dataCy = 'check-list-item', onEditAmounts, onSelect, timeZone }) => {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);

  const renderHeader = () => (
    <HeaderRow data-cy={`${dataCy}-header`} ignored={isIgnored}>
      {isIgnored && (
        <>
          <Text size="small" fontWeight="medium">
            Ignored Check - It will not be included in your payout
          </Text>
        </>
      )}
      {isSplit && (
        <>
          <Text size="small" fontWeight="medium">
            Split Check
          </Text>
        </>
      )}
    </HeaderRow>
  );

  const renderSplitDetails = () => {
    const { shares } = check.override as IPOSTicketSplitOverride;
    const { tips, autograts } = shares.reduce(
      (acc, share) => {
        if (share.sources.length === 1) {
          if (share.sources[0] === RuleFundSource.TIPS) {
            acc.tips.push(share);
          } else {
            acc.autograts.push(share);
          }
        }
        return acc;
      },
      { tips: [], autograts: [] }
    );
    const separateSplit = tips.length > 0 && autograts.length > 0;
    return (
      <DetailsWrapper data-cy={`${dataCy}-split-details`}>
        {!separateSplit ? (
          displayShares(shares)
        ) : (
          <>
            {displayShares(tips, 'Tips')}
            <HR />
            {displayShares(autograts, 'Autograts')}
          </>
        )}
      </DetailsWrapper>
    );
  };

  const buildToolTipMessage = useMemo(
    () => (
      <ToolTipMessage>
        {check.openedAt && (
          <ToolTipMessageItem>
            <Text>Opened</Text>
            <Text>{getDateTimeWithoutZone(check.openedAt, timeZone)}</Text>
          </ToolTipMessageItem>
        )}
        {check.paidAt && (
          <ToolTipMessageItem>
            <Text>Paid</Text>
            <Text>{getDateTimeWithoutZone(check.paidAt, timeZone)}</Text>
          </ToolTipMessageItem>
        )}
        {check.closedAt && (
          <ToolTipMessageItem>
            <Text>Closed</Text>
            <Text>{getDateTimeWithoutZone(check.closedAt, timeZone)}</Text>
          </ToolTipMessageItem>
        )}
      </ToolTipMessage>
    ),
    [check]
  );

  const isIgnored = useMemo(() => check.override && check.override.type === TicketOverrideType.IGNORE, [check]);
  const isSplit = useMemo(() => check.override && check.override.type === TicketOverrideType.SPLIT, [check]);

  return (
    <Layout data-cy={dataCy} ignored={isIgnored} ref={ref}>
      {(isIgnored || isSplit) && renderHeader()}
      <TopRow data-cy={`${dataCy}-top-row`}>
        <MainContent>
          <DropdownIconWrapper
            data-cy={`${dataCy}-accordion-trigger`}
            onClick={() => {
              setOpen(!open);
            }}
          >
            <DropdownIcon className={open ? 'open' : ''} icon="Dropdown" fill={theme.color.neutral[500]} />
          </DropdownIconWrapper>
          <CheckInfoWrapper data-cy={`${dataCy}-check-info`}>
            <TopRowItem>
              <StyledTag label={check.segmentName} size={Size.SMALL} appearance={Appearance.NEUTRAL} ignored={isIgnored} />
              <Label className="display-id">#{check.displayId}</Label>
              <TooltipWrapper>
                <Text className="check-time">{getDateTimeWithoutZone(check.processingTime, timeZone)}</Text>
                <TooltipIcon id={`field-tooltip-${check.displayId}`} place="top">
                  {buildToolTipMessage}
                </TooltipIcon>
              </TooltipWrapper>
            </TopRowItem>
          </CheckInfoWrapper>
          <TopRowItem data-cy={`${dataCy}-employee`} hideOnMobile>
            <Label>Employee</Label>
            <Text className="word-break">{check.employeeName}</Text>
          </TopRowItem>
          <TopRowItem data-cy={`${dataCy}-tips`} hideOnMobile>
            <Label>Tips</Label>
            <Text>{centsToDollarsDisplay(check.totals.tip.gross)}</Text>
          </TopRowItem>
          <TopRowItem data-cy={`${dataCy}-autograts`} hideOnMobile>
            <Label>Autograts</Label>
            <Text>{centsToDollarsDisplay(check.totals.autoGratuity.gross)}</Text>
          </TopRowItem>
          <TopRowItem data-cy={`${dataCy}-total`} hideOnMobile>
            <Label>Total</Label>
            <Text>{centsToDollarsDisplay(check.gross)}</Text>
          </TopRowItem>
        </MainContent>
        <Controls data-cy={`${dataCy}-controls`}>
          <Button data-cy={`${dataCy}-edit-button`} label="Edit" onClick={() => onEditAmounts(check)} size="small" appearance="white" disabled={isIgnored || isSplit} />
          <Checkbox
            size="large"
            data-cy={`${dataCy}-check-select`}
            id={`${check.ticketId}-check-select`}
            checked={!!check.selected}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              onSelect(check, e.target.checked);
            }}
          />
        </Controls>
      </TopRow>
      <MobileTopRow data-cy={`${dataCy}-mobile-top-row`}>
        <TopRowItem data-cy={`${dataCy}-employee`}>
          <Label>Employee</Label>
          <Text>{check.employeeName}</Text>
        </TopRowItem>
        <TopRowItem data-cy={`${dataCy}-tips`}>
          <Label>Tips</Label>
          <Text>{centsToDollarsDisplay(check.totals.tip.gross)}</Text>
        </TopRowItem>
        <TopRowItem data-cy={`${dataCy}-autograts`}>
          <Label>Autograts</Label>
          <Text>{centsToDollarsDisplay(check.totals.autoGratuity.gross)}</Text>
        </TopRowItem>
        <TopRowItem data-cy={`${dataCy}-total`}>
          <Label>Total</Label>
          <Text>{centsToDollarsDisplay(check.gross)}</Text>
        </TopRowItem>
      </MobileTopRow>
      {open && isSplit && renderSplitDetails()}
      {open && <CheckDetails check={check} categoryGroups={categoryGroups} creditCardFee={creditCardFee} />}
    </Layout>
  );
};

export default CheckListItem;
